import { useState, type ChangeEvent } from 'react';
import { Chip, Surface, type Tone } from '@nexvia-logistics/ui';
import { FileText } from '@nexvia-logistics/ui/icons';
import { formatCalendarDateShort } from '@/shared/format';
import { CheckIcon } from '@/shared/icons';
import { CHIP_WRAP } from '@/shared/chip-wrap';
import {
  ACCEPTED_DOCUMENT_MIME_TYPES,
  documentStatusPresentation,
  effectiveDocumentStatus,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
  MIN_DOCUMENT_FILE_SIZE_BYTES,
  STALLED_UPLOAD_PRESENTATION,
  STORAGE_DOCUMENT_LABELS,
  type StatusTone
} from './storage-documents.constants';
import {
  confirmDocumentUpload,
  describeDocumentUploadError,
  uploadDocument,
  type StorageDocument,
  type StorageDocumentType
} from './storage-api';

export interface DocumentUploadRowProps {
  documentType: StorageDocumentType;
  document?: StorageDocument;
  /** Parent owns the document list; this row only signals "something changed, refetch it". */
  onChanged: () => void;
}

/**
 * Fix 4d (Claude Design mockup): this row's status chip used to be a local, hardcoded
 * `rounded-full ... text-xs` map — exactly `docs/TECHNICAL-AUDIT.md`'s F-017 duplication finding,
 * with its own "no production caller yet" interim state now closed by this call site. Reuses the
 * real `Chip` atom from `@nexvia-logistics/ui` instead.
 *
 * `positive` → `active`, not Chip's own `positive`, is the one deliberate remap, not a 1:1 rename:
 * `documentStatusPresentation`'s `positive` tone is only ever "Aprobado", and the design wants that
 * specific chip as a solid blue pill with white text (Chip's `active` — real tokens `bg-tone-
 * active-bg`/`text-tone-active-fg`, confirmed against this file's own test), not Chip's own green
 * `positive` (which is what today's hardcoded map already rendered, and exactly the "sale verde...
 * en vez de pill azul" gap this fix closes).
 * Every other status keeps its closest same-named Chip tone, visually unchanged.
 *
 * **NEX-1164 design pass (2026-10-18, spec §3.6)**: "Aprobado" moves from `active` (solid blue) to
 * `info` (light blue) with its check — the mock's chip. Still never Chip's green `positive`.
 */
const CHIP_TONE_BY_STATUS_TONE: Record<StatusTone, Tone> = {
  neutral: 'neutral',
  info: 'info',
  positive: 'info',
  caution: 'caution',
  critical: 'critical'
};

/** `expiresAt` is a bare `YYYY-MM-DD` or an ISO datetime: its UTC calendar day, with the mock's short
 *  month ("30 nov. 2026") — `formatCalendarDateShort` reads it in UTC, so the runtime's own timezone
 *  never shifts it a day back (see `storage-documents.constants.ts`'s `isDocumentExpired` comment
 *  for the same pitfall). Not `formatShortDate`: that one reads a genuine instant locally and has no
 *  year. */
function formatDate(value: string): string {
  return formatCalendarDateShort(value.split('T')[0]) ?? value;
}

export function DocumentUploadRow({ documentType, document, onChanged }: DocumentUploadRowProps) {
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const status = document ? effectiveDocumentStatus(document) : null;
  // NEX-1164 / NEX-1171 — `uploading` on the server with no upload running in this tab: it never
  // finished (a failed PUT to Spaces leaves the row there), so it is offered again instead of
  // reading "Subiendo…" forever.
  const stalledUpload = document?.status === 'uploading' && !busy;
  const chip = stalledUpload
    ? STALLED_UPLOAD_PRESENTATION
    : status
      ? documentStatusPresentation(status)
      : { label: 'Sin subir', tone: 'neutral' as StatusTone };
  const underReview = document?.status === 'pending_review';
  const disabled = busy || underReview;
  const actionLabel = stalledUpload ? 'Volver a subir' : document ? 'Reemplazar' : 'Subir';
  const accessibleLabel = `${actionLabel} ${STORAGE_DOCUMENT_LABELS[documentType]}`;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // lets the same filename be re-selected later (e.g. after a failure)
    if (!file) return;

    setErrorMessage(null);

    if (!ACCEPTED_DOCUMENT_MIME_TYPES.includes(file.type)) {
      setErrorMessage('Formato no admitido. Subí un PDF, JPG, PNG, HEIC o WEBP.');
      return;
    }
    if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      setErrorMessage('El archivo pesa más de 10 MB.');
      return;
    }
    if (file.size < MIN_DOCUMENT_FILE_SIZE_BYTES) {
      setErrorMessage('El archivo pesa menos de 10 KB: puede que esté vacío o dañado. Elegí otro archivo.');
      return;
    }

    setBusy(true);
    try {
      const { documentId } = await uploadDocument(documentType, file);
      await confirmDocumentUpload(documentId);
      onChanged();
    } catch (err) {
      // NEX-1164 — mapped by code: the API's English message or a raw "Failed to fetch" never shows.
      setErrorMessage(describeDocumentUploadError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    // NEX-1164 design pass (spec 2026-10-18 §3.6): the mock's row — an inset surface, the document
    // glyph, the name over "Vence el …", the chip and the upload control on the right.
    <Surface variant="inset" padding="sm" className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <FileText size={20} className="mt-0.5 shrink-0 text-on-surface-variant" />
        <div className="flex min-w-0 flex-col gap-1">
          {/* The document's own name: this is content being displayed (what document this row is),
           *  not a field label, so it takes the `ui-body` role like any other piece of shown data —
           *  `font-medium` is dropped because `text-ui-body` already bundles weight 500, the exact
           *  same weight `font-medium` was setting. */}
          <span className="text-ui-body text-on-surface">{STORAGE_DOCUMENT_LABELS[documentType]}</span>
          {/* Expiry/rejection/review-note/error lines are secondary inline meta text under the row's
           *  main content, not a form field's own helper text — `ui-small` (13px/500), not `ui-
           *  caption` (12px/600, reserved below for actual form-field validation in
           *  `FiscalDataForm.tsx`), per the migration guide's own "judge by role" instruction. */}
          {document?.expiresAt && <span className="text-ui-small text-on-surface-variant">Vence el {formatDate(document.expiresAt)}</span>}
          {document?.status === 'rejected' && document.rejectionReason && (
            <p className="text-ui-small text-error-base">{document.rejectionReason}</p>
          )}
          {underReview && <p className="text-ui-small text-on-surface-variant">No se puede reemplazar mientras está en revisión.</p>}
          {errorMessage && (
            <p role="alert" className="text-ui-small text-error-base">
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      {/* Fix 4e: the status chip gets its own right-aligned column, separate from the upload
       *  button — previously it sat inside the left (name) column, in the same visual group as
       *  the upload action below. */}
      <div className="flex flex-wrap items-center gap-3">
        <Chip
          tone={CHIP_TONE_BY_STATUS_TONE[chip.tone]}
          icon={chip.tone === 'positive' ? <CheckIcon size={12} /> : undefined}
          className={CHIP_WRAP}
        >
          {chip.label}
        </Chip>

        {/*
         * NEX-1151: kept hand-rolled rather than adopted as the real `Button`/`IconButton` atom.
         * Both atoms only ever render a `<button>` or an `<a>` (see `Button.d.ts`'s `AsButton`/
         * `AsAnchor`), never a `<label>` wrapping a hidden file input — the real interaction here
         * is "click anywhere in this label to open the native file picker for the input it wraps",
         * which neither atom supports without restructuring to a ref-triggered hidden input, a
         * bigger behavioral change than this migration's "classNames + clean drop-in" scope, and
         * risky against this file's own heavily-tested upload/disabled-state behavior. Only the
         * classes are migrated. `rounded-pill` (not the table's fallback `rounded-card`) because
         * this control visually IS a button, and the real `Button` atom is always pill-shaped
         * regardless of size — the closer visual match to what adopting `Button` would look like.
         */}
        <label
          aria-disabled={disabled}
          className="inline-flex min-h-11 w-fit cursor-pointer items-center rounded-pill border border-primary px-4 py-2 text-ui-label text-primary aria-disabled:cursor-not-allowed aria-disabled:border-outline-muted aria-disabled:text-on-surface-placeholder"
        >
          {busy ? 'Subiendo…' : actionLabel}
          <input
            type="file"
            className="sr-only"
            aria-label={accessibleLabel}
            accept={ACCEPTED_DOCUMENT_MIME_TYPES.join(',')}
            disabled={disabled}
            onChange={(event) => void handleFileChange(event)}
          />
        </label>
      </div>
    </Surface>
  );
}
